<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { parseYhSentence } from './yongsinUsage.js';
import { transformMarkedText } from '@/composables/splitSentence';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import Yongsin24Hours from './Yongsin24Hours.vue';
import LuckyNumbers from './LuckyNumbers.vue';
import YongHeeDataPair from './YongHeeDataPair.vue';

const props = defineProps({
  yData: Object,
  isMobile: Boolean,
});

// 색상 데이터
const colorStrategy = computed(() => props.yData?.분석상세?.용희활용?.색상전략?.제목);
const strategyDesc = computed(() => props.yData?.분석상세?.용희활용?.색상전략?.설명);
const yongsinColorSentence = computed(() => props.yData?.분석상세?.용희활용?.색상전략?.용신색상);
const heesinColorSentence = computed(() => props.yData?.분석상세?.용희활용?.색상전략?.희신색상);
const yongsinColorCard = computed(() => {
  const { colors, description } = parseYhSentence(yongsinColorSentence.value, 'color');
  return { title: '용신색상', tone: 'neutral', colors, description };
});
const heesinColorCard = computed(() => {
  const { colors, description } = parseYhSentence(heesinColorSentence.value, 'color');
  return { title: '희신색상', tone: 'neutral', colors, description };
});
const colorCards = computed(() => [yongsinColorCard.value, heesinColorCard.value]);
const colorAdvice = computed(() => props.yData?.분석상세?.용희활용?.색상전략?.실용조언);

// 숫자 데이터
const useNumbers = computed(() => props.yData?.분석상세?.용희활용?.숫자활용?.제목);
const numberDesc = computed(() => props.yData?.분석상세?.용희활용?.숫자활용?.설명);
const yongsinNumberSentence = computed(() => props.yData?.분석상세?.용희활용?.숫자활용?.용신숫자);
const heesinNumberSentence = computed(() => props.yData?.분석상세?.용희활용?.숫자활용?.희신숫자);
const yongsinNumberCard = computed(() => {
  const { numbers, description } = parseYhSentence(yongsinNumberSentence.value, 'number');
  return { title: '용신숫자', tone: 'warm', numbers, description };
});
const heesinNumberCard = computed(() => {
  const { numbers, description } = parseYhSentence(heesinNumberSentence.value, 'number');
  return { title: '희신숫자', tone: 'warm', numbers, description };
});
const numberCards = computed(() => [yongsinNumberCard.value, heesinNumberCard.value]);
const numberAdvice = computed(() => props.yData?.분석상세?.용희활용?.숫자활용?.실용조언);

// 시공간 데이터
const useTimePlace = computed(() => props.yData?.분석상세?.용희활용?.시공간활용?.제목);
const TPDesc = computed(() => props.yData?.분석상세?.용희활용?.시공간활용?.설명);
const yongsinTPSentence = computed(() => props.yData?.분석상세?.용희활용?.시공간활용?.용신시공간);
const heesinTPSentence = computed(() => props.yData?.분석상세?.용희활용?.시공간활용?.희신시공간);
const yongsinTPCard = computed(() => {
  const { directions, description } = parseYhSentence(yongsinTPSentence.value, 'direction');
  return { title: '용신의 시공간', tone: 'yong-primary', directions, description };
});
const heesinTPCard = computed(() => {
  const { directions, description } = parseYhSentence(heesinTPSentence.value, 'direction');
  return { title: '희신의 시공간', tone: 'yong-primary', directions, description };
});
const timePlaceCards = computed(() => [yongsinTPCard.value, heesinTPCard.value]);
const timePlaceAdvice = computed(() => props.yData?.분석상세?.용희활용?.시공간활용?.실용조언);

const yongTimeChartData = computed(
  () => props.yData?.분석상세?.용희활용?.용희원소?.용신_원본?.지지,
);
const yongFiveChartData = computed(() => props.yData?.분석상세?.용희활용?.요약정보?.용신?.오행);
const heeTimeChartData = computed(() => props.yData?.분석상세?.용희활용?.용희원소?.희신_원본?.지지);
const heeFiveChartData = computed(() => props.yData?.분석상세?.용희활용?.요약정보?.희신?.오행);
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="personality-wrapper st-gap-24 content_info_area_box">
          <div class="yongsin-cont-box">
            <div class="yongsin-cont__tit-section">
              <span class="main-tit">
                🎨 {{ colorStrategy || '색상으로 용신과 희신 에너지를 활용하는 방법' }}
              </span>
              <span class="sub-tit">
                {{ strategyDesc || '특별한 순간, 색상 에너지로 기운을 더해보세요.' }}
              </span>
            </div>
            <div class="yongsin-cont__body gray-box">
              <YongHeeDataPair :items="colorCards" />
              <div class="yongsin-cont__inner-desc">
                <div class="yongsin-cont__inner-tit">활용 Tip</div>
                <!-- <div class="yongsin-cont__inner-desc">
                  {{ colorAdvice }}
                </div> -->
                <div class="yongsin-cont__inner-desc" v-html="transformMarkedText(colorAdvice)" />
              </div>
            </div>
          </div>
          <div class="yongsin-cont-box">
            <div class="yongsin-cont__tit-section">
              <span class="main-tit"> 🔢 {{ useNumbers || '숫자를 활용하는 방법' }} </span>
              <span class="sub-tit">
                {{ numberDesc || '나에게 힘이 되는 숫자를 일상 속 작은 선택에 담아보세요.' }}
              </span>
            </div>
            <div class="yongsin-cont__body gray-box">
              <!-- <YongHeeDataPair :items="numberCards" /> -->
              <LuckyNumbers :numbers="numberCards" :isMobile="isMobile" />
              <div class="yongsin-cont__inner-desc">
                <div class="yongsin-cont__inner-tit">활용 Tip</div>
                <!-- <div class="yongsin-cont__inner-desc">
                  {{ numberAdvice }}
                </div> -->
                <div class="yongsin-cont__inner-desc" v-html="transformMarkedText(numberAdvice)" />
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper content_info_area_box">
          <div class="yongsin-cont-box">
            <div class="yongsin-cont__tit-section">
              <span class="main-tit">
                🧭 {{ useTimePlace || '시간, 공간, 방향으로 활용하는 방법' }}
              </span>
              <span class="sub-tit">
                {{ TPDesc || '용신과 희신을 시공간 상황에 맞게 활용하는 방법입니다.' }}
              </span>
            </div>
            <div class="yongsin-cont__body gray-box">
              <div class="yongsin-cont__chart-area">
                <Yongsin24Hours
                  :yongTimeChartData="yongTimeChartData"
                  :heeTimeChartData="heeTimeChartData"
                  :yongFiveChartData="yongFiveChartData"
                  :heeFiveChartData="heeFiveChartData"
                  :isMobile="isMobile"
                />
              </div>
              <div class="yong-cont__divider" />
              <YongHeeDataPair :items="timePlaceCards" :isMobile="isMobile" />
              <div class="yongsin-cont__inner-desc">
                <div class="yongsin-cont__inner-tit">활용 Tip</div>
                <!-- <div class="yongsin-cont__inner-desc">
                  {{ timePlaceAdvice }}
                </div> -->
                <div
                  class="yongsin-cont__inner-desc"
                  v-html="transformMarkedText(timePlaceAdvice)"
                />
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
</style>
